import React, { useState, useEffect } from 'react';
import {
  Grid,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import { withTranslation } from 'next-i18next';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import WidgetDateTimeField from 'common/components/MainWidgetDialog/WidgetDateTimeField';
import WidgetLocationField from 'common/components/MainWidgetDialog/WidgetLocationField';
import { LOCATION_TYPE } from 'common/components/MainWidgetDialog/utils/mainWidgetConstants';
import VehicleCount from 'common/components/MainWidgetDialog/VehicleCount';
import WidgetNotificationBox from 'common/components/MainWidgetDialog/WidgetNotificationBox';

const useStyles = makeStyles((theme) => ({
  rentalWidget: {
    alignItems: 'center',
  },
  RWCheckBox: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  RWSearchNowButton: {
    lineHeight: 'unset',
    height: '100%',
  },
  RWArrowIcon: {
    position: 'absolute',
    display: 'flex',
    alignItem: 'center',
    justifyContent: 'center',
    width: theme.typography.pxToRem(35),
    height: theme.typography.pxToRem(35),
    top: '50%',
    transform: 'translateY(-50%)',
    right: theme.typography.pxToRem(-20),
    zIndex: 2,
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.grey[200]}`,
    borderRadius: '2px',
    '& svg': {
      width: '100%',
      height: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  RWLeftTF: {
    position: 'relative',
  },
}));

const ShiftedLocationFeild = withStyles((theme) => ({
  textFieldLabel: ({ vertical, selectedLocationId }) => ({
    backgroundColor: selectedLocationId ? theme.palette.common.white : theme.palette.grey[50],
    overflow: 'hidden',

    '& .IconedBoxContainer': {
      [theme.breakpoints.up('md')]: {
        left: vertical ? null : 10,
      },
      '& .MuiFilledInput-root': {
        backgroundColor: selectedLocationId ? theme.palette.common.white : theme.palette.grey[50],
        border: 'none',
      },
      '& .Mui-error': {
        '&:before, :after': {
          left: vertical ? null : `-${10}px`,
        },
      },
    },
  }),
}))(WidgetLocationField);

const SHIFT_AMOUNT = 10;

const RentalWidget = (props) => {
  const {
    t,
    pickupData,
    dropoffData,
    motorcycleList,
    handleLocationInputChange,
    formData,
    handleFormDataChange,
    shouldDisableDate,
    returnToDifferentLocation,
    handleReturnToDifferentLocation,
    isOptionsLoading,
    handleSearchButtonClick,
    renderAllWidget = true,
    handleRenderAllWidget = () => {},
    bordered,
    rentalWidgetNotifications,
    disableVehicleCount,
    actionButtonCustomLabel,
    errors,
    disabled,
    hasToReturnToDifferentLocation,
    isDealWidget,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [widgetMounted, setWidgetMounted] = useState(false);
  useEffect(() => {
    setWidgetMounted(true);
  }, []);

  useEffect(() => {
    if (widgetMounted) {
      if (isMobile) {
        if (formData.dataFromQuery) {
          handleReturnToDifferentLocation(true, true);
          if (
            formData.reservation?.pickup_location_id == formData.reservation?.dropoff_location_id
          ) {
            handleReturnToDifferentLocation(false);
          }
          handleRenderAllWidget(true);
        } else {
          handleReturnToDifferentLocation(false);
        }
      } else {
        handleReturnToDifferentLocation(true, true);
        handleRenderAllWidget(true);
      }
    }
  }, [widgetMounted, isMobile]);
  return (
    <Grid container spacing={2} className={classes.rentalWidget}>
      {((rentalWidgetNotifications?.[0])
        || errors?.reservation_widget_base) && (
        <Grid item xs={12}>
          {errors?.reservation_widget_base && (
            <Box>
              <WidgetNotificationBox
                title={t('fe_es_common_main_widget:base_error_header')}
                notificationsList={errors?.reservation_widget_base}
              />
            </Box>
          )}
          {rentalWidgetNotifications?.[0] && (
            <Box>
              <WidgetNotificationBox notificationsList={rentalWidgetNotifications} />
            </Box>
          )}
        </Grid>
      )}
      <Grid container item spacing={1}>
        <Grid item xs={12} md={6} className={classes.RWLeftTF}>
          <WidgetLocationField
            pickup={LOCATION_TYPE.PICKUP}
            selectedLocationId={formData.reservation?.pickup_location_id}
            locationsList={pickupData.locations}
            bordered={bordered}
            label={t('fe_es_common_main_widget:pickup_location')}
            placeholder={t('fe_es_common_main_widget:choose_pickup_location')}
            handleInputChange={handleLocationInputChange}
            handleLocationChange={handleFormDataChange}
            handleRenderAllWidget={handleRenderAllWidget}
            loading={isOptionsLoading[LOCATION_TYPE.PICKUP]}
            errors={errors}
            hasArrow="true"
            disabled={disabled}
          />
          <Box className={classes.RWArrowIcon}>
            <ArrowRightAltIcon />
          </Box>
        </Grid>
        {renderAllWidget && (
          <>
            <Grid item xs={12} className={classes.RWCheckBox}>
              <Box>
                <FormGroup>
                  <FormControlLabel
                    checked={returnToDifferentLocation}
                    onChange={(e) => handleReturnToDifferentLocation(e.target.checked)}
                    control={<Checkbox defaultChecked />}
                    label={t('fe_es_common_main_widget:return_to_different_location')}
                    disabled={disabled}
                  />
                </FormGroup>
              </Box>
            </Grid>
            {returnToDifferentLocation && (
              <Grid item xs={12} md={6}>
                <ShiftedLocationFeild
                  SHIFT_AMOUNT={SHIFT_AMOUNT}
                  pickup={LOCATION_TYPE.DROPOFF}
                  selectedLocationId={formData.reservation?.dropoff_location_id}
                  locationsList={dropoffData.locations}
                  bordered={bordered}
                  hasArrow="false"
                  shiftAmount={SHIFT_AMOUNT}
                  label={t('fe_es_common_main_widget:dropoff_location')}
                  placeholder={
                    hasToReturnToDifferentLocation
                      ? t('fe_es_common_main_widget:choose_dropoff_location')
                      : t('fe_es_common_main_widget:same_as_pickup_location')
                  }
                  handleInputChange={handleLocationInputChange}
                  handleLocationChange={handleFormDataChange}
                  loading={isOptionsLoading[LOCATION_TYPE.DROPOFF]}
                  errors={errors}
                  disabled={disabled}
                  isDealWidget={isDealWidget}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid container item spacing={2}>
        {renderAllWidget && (
          <>
            <Grid item xs={12} md={6}>
              <WidgetDateTimeField
                pickup={LOCATION_TYPE.PICKUP}
                bordered={bordered}
                timeList={pickupData.timeSlots}
                label={t('fe_es_common_main_widget:pickup_date_label')}
                date={formData.reservation?.pickup_date}
                time={formData.reservation?.pickup_time_slot}
                minDate={pickupData.minDate}
                maxDate={pickupData.maxDate}
                handleDateTimeChange={handleFormDataChange}
                shouldDisableDate={shouldDisableDate}
                errors={errors}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <WidgetDateTimeField
                pickup={LOCATION_TYPE.DROPOFF}
                bordered={bordered}
                timeList={dropoffData.timeSlots}
                label={t('fe_es_common_main_widget:dropoff_date_label')}
                date={formData.reservation?.dropoff_date}
                time={formData.reservation?.dropoff_time_slot}
                minDate={dropoffData.minDate}
                maxDate={dropoffData.maxDate}
                handleDateTimeChange={handleFormDataChange}
                shouldDisableDate={shouldDisableDate}
                errors={errors}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <VehicleCount
                errors={errors}
                disableVehicleCount={disableVehicleCount || disabled}
                handleFormDataChange={handleFormDataChange}
                formData={formData}
                bordered={bordered}
                motorcycleList={motorcycleList}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={6}>
          <Button
            variant="contained"
            className={classes.RWSearchNowButton}
            id="rentalWidgetSearchNowBtn"
            fullWidth
            size="large"
            color="primary"
            disableElevation
            disabled={disabled}
            onClick={() => {
              if (renderAllWidget) {
                handleSearchButtonClick();
              } else {
                handleRenderAllWidget(true);
              }
            }}
          >
            {actionButtonCustomLabel
              || (renderAllWidget
                ? t('fe_es_common_main_widget:search_now')
                : t('fe_es_common_main_widget:start_your_reservation'))}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withTranslation(['fe_es_common_main_widget'])(RentalWidget);
